/**
 * Core Web Vitals Metrics Collection
 * 
 * Envia métricas de performance para o Reserve Connect
 * Baseado na API Web Vitals
 */

import { trackEvent } from './telemetry';

interface Metric {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  delta?: number;
  id?: string;
}

// Thresholds baseados nas recomendações do Google
const thresholds: Record<string, { good: number; poor: number }> = {
  CLS: { good: 0.1, poor: 0.25 },
  FID: { good: 100, poor: 300 },
  LCP: { good: 2500, poor: 4000 },
  FCP: { good: 1800, poor: 3000 },
  TTFB: { good: 800, poor: 1800 },
  INP: { good: 200, poor: 500 },
};

function getRating(name: string, value: number): Metric['rating'] {
  const threshold = thresholds[name];
  if (!threshold) return 'good';
  
  if (value <= threshold.good) return 'good';
  if (value <= threshold.poor) return 'needs-improvement';
  return 'poor';
}

/**
 * Reporta métrica de performance
 */
function reportMetric(metric: Metric) {
  // Log em dev
  if (import.meta.env.DEV) {
    console.log(`[Web Vitals] ${metric.name}:`, metric.value, metric.rating);
  }

  // Envia para telemetria
  void trackEvent('web_vitals', {
    metric_name: metric.name,
    metric_value: metric.value,
    metric_rating: metric.rating,
    page_path: window.location.pathname,
  });

  // Envia para GA4 (se disponível)
  if (window.gtag) {
    window.gtag('event', 'web_vitals', {
      event_category: 'Web Vitals',
      event_label: metric.name,
      value: Math.round(metric.value),
      metric_rating: metric.rating,
    });
  }
}

/**
 * Coleta métricas Web Vitals
 * 
 * Uso: import { collectWebVitals } from './webVitals';
 *      collectWebVitals();
 */
export function collectWebVitals() {
  // Só executa no browser
  if (typeof window === 'undefined') return;

  // CLS - Cumulative Layout Shift
  let clsValue = 0;
  const clsEntries: PerformanceEntry[] = [];

  const clsObserver = new PerformanceObserver((list) => {
    for (const entry of list.getEntries()) {
      if (!(entry as PerformanceEntry & { hadRecentInput: boolean }).hadRecentInput) {
        clsEntries.push(entry);
        clsValue += (entry as PerformanceEntry & { value: number }).value;
      }
    }
  });

  try {
    clsObserver.observe({ entryTypes: ['layout-shift'] as const });
  } catch {
    // Browser não suporta
  }

  // Reporta CLS quando a página é oculta
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden' && clsValue > 0) {
      reportMetric({
        name: 'CLS',
        value: Math.round(clsValue * 1000) / 1000,
        rating: getRating('CLS', clsValue),
      });
      clsValue = 0;
    }
  });

  // LCP - Largest Contentful Paint
  let lcpValue = 0;
  const lcpObserver = new PerformanceObserver((list) => {
    const entries = list.getEntries();
    const lastEntry = entries[entries.length - 1];
    lcpValue = lastEntry.startTime;
  });

  try {
    lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] as const });
  } catch {
    // Browser não suporta
  }

  // Reporta LCP quando a página é oculta
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden' && lcpValue > 0) {
      reportMetric({
        name: 'LCP',
        value: Math.round(lcpValue),
        rating: getRating('LCP', lcpValue),
      });
    }
  });

  // FCP - First Contentful Paint
  const fcpObserver = new PerformanceObserver((list) => {
    for (const entry of list.getEntries()) {
      if (entry.name === 'first-contentful-paint') {
        reportMetric({
          name: 'FCP',
          value: Math.round(entry.startTime),
          rating: getRating('FCP', entry.startTime),
        });
      }
    }
  });

  try {
    fcpObserver.observe({ entryTypes: ['paint'] as const });
  } catch {
    // Browser não suporta
  }

  // TTFB - Time to First Byte
  const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
  if (navigation) {
    const ttfb = navigation.responseStart - navigation.startTime;
    reportMetric({
      name: 'TTFB',
      value: Math.round(ttfb),
      rating: getRating('TTFB', ttfb),
    });
  }

  // FID - First Input Delay (simulado via Event Timing API se disponível)
  try {
    const fidObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        const eventEntry = entry as PerformanceEntry & { processingStart: number; startTime: number };
        const delay = eventEntry.processingStart - eventEntry.startTime;
        if (delay > 0) {
          reportMetric({
            name: 'FID',
            value: Math.round(delay),
            rating: getRating('FID', delay),
          });
        }
      }
    });
    fidObserver.observe({ entryTypes: ['first-input'] as const });
  } catch {
    // Browser não suporta
  }

  // INP - Interaction to Next Paint (Core Web Vital, replaces FID)
  let worstInteractionDelay = 0;
  const inpInteractions = new Map<string, { start: number; delay: number }>();

  try {
    const inpObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        const e = entry as PerformanceEntry & { interactionId?: number; name: string; startTime: number; processingStart: number; duration: number };
        if (e.interactionId === undefined || e.interactionId === null) continue;
        if (e.name === 'pointerdown') continue; // pointerdown is a duplicate of pointerup

        const id = String(e.interactionId);
        const existing = inpInteractions.get(id);

        if (!existing) {
          inpInteractions.set(id, { start: e.startTime, delay: e.processingStart - e.startTime });
          continue;
        }

        const duration = e.startTime + e.duration - existing.start;
        worstInteractionDelay = Math.max(worstInteractionDelay, Math.round(duration));
        inpInteractions.delete(id);
      }
    });

    inpObserver.observe({ entryTypes: ['event'] as const, buffered: true });

    // Report INP on page hide
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden' && worstInteractionDelay > 0) {
        reportMetric({
          name: 'INP',
          value: worstInteractionDelay,
          rating: getRating('INP', worstInteractionDelay),
        });
      }
    });
  } catch {
    // Browser não suporta
  }
}
